/**
 * WordPress dependencies
 */
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { Spinner, Notice } from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';

/**
 * Internal dependencies
 */
import BookingListTable from './BookingListTable';
import BookingFilters from './BookingFilters';
import Pagination from './Pagination';
import { useDebounce } from '../utils/useDebounce';

const DEFAULT_FILTERS = {
	status: '',
	event_id: 0,
	date_from: '',
	date_to: '',
};
const DEFAULT_SORT = { orderby: 'created_at', order: 'DESC' };

const BookingList = ( { onEditBooking, onCreateNew } ) => {
	const [ bookings, setBookings ] = useState( [] );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ notices, setNotices ] = useState( [] );

	// Pagination state
	const [ currentPage, setCurrentPage ] = useState( 1 );
	const [ itemsPerPage, setItemsPerPage ] = useState( 10 );
	const [ totalItems, setTotalItems ] = useState( 0 );
	const [ totalPages, setTotalPages ] = useState( 1 );

	// Filter + sort state
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ filters, setFilters ] = useState( DEFAULT_FILTERS );
	const [ sort, setSort ] = useState( DEFAULT_SORT );

	// Debounce the search input so live search fires once per pause, not per keystroke.
	const searchQuery = useDebounce( searchInput, 400 );

	const hasActiveFilters =
		searchInput !== '' ||
		filters.status !== '' ||
		filters.event_id !== 0 ||
		filters.date_from !== '' ||
		filters.date_to !== '';

	// Load bookings with pagination, filters, sorting, and search.
	const loadBookings = async (
		page = currentPage,
		perPage = itemsPerPage,
		filterParams = filters,
		search = searchQuery,
		sortParams = sort
	) => {
		try {
			setIsLoading( true );

			// Build query parameters
			const params = new URLSearchParams( {
				page: page.toString(),
				per_page: perPage.toString(),
			} );

			if ( filterParams.status ) {
				params.set( 'status', filterParams.status );
			}
			if ( filterParams.event_id ) {
				params.set( 'event_id', filterParams.event_id );
			}
			if ( filterParams.date_from ) {
				params.set( 'date_from', filterParams.date_from );
			}
			if ( filterParams.date_to ) {
				params.set( 'date_to', filterParams.date_to );
			}
			if ( search ) {
				params.set( 'search', search );
			}
			if ( sortParams.orderby ) {
				params.set( 'orderby', sortParams.orderby );
			}
			if ( sortParams.order ) {
				params.set( 'order', sortParams.order );
			}

			const response = await apiFetch( {
				path: `/nemtly-booking/v1/bookings?${ params.toString() }`,
			} );

			// Handle response format from Booking API
			if ( response.success && response.result && response.pagination ) {
				setBookings(
					Array.isArray( response.result ) ? response.result : []
				);
				setTotalItems( response.pagination.total_items || 0 );
				setTotalPages( response.pagination.total_pages || 1 );
				setCurrentPage( response.pagination.current_page || page );

				// Clear any previous notices on successful load
				setNotices( [] );
			} else if ( response.success === false ) {
				// Handle API error response
				throw new Error(
					response.message ||
						__( 'Failed to load bookings.', 'nemtly-booking' )
				);
			} else {
				// Fallback for unexpected response format
				setBookings( [] );
				setTotalItems( 0 );
				setTotalPages( 1 );
				setNotices( [
					{
						type: 'warning',
						message: __(
							'Unexpected response format from server.',
							'nemtly-booking'
						),
					},
				] );
			}
		} catch ( error ) {
			setNotices( [
				{
					type: 'error',
					message:
						error.message ||
						__( 'Failed to load bookings.', 'nemtly-booking' ),
				},
			] );
			setBookings( [] );
			setTotalItems( 0 );
			setTotalPages( 1 );
		} finally {
			setIsLoading( false );
		}
	};

	// Load bookings on component mount and when pagination/filters/search/sort change.
	useEffect( () => {
		loadBookings( currentPage, itemsPerPage, filters, searchQuery, sort );
	}, [
		currentPage,
		itemsPerPage,
		searchQuery,
		filters.status,
		filters.event_id,
		filters.date_from,
		filters.date_to,
		sort.orderby,
		sort.order,
	] );

	/**
	 * Handle page change
	 *
	 * @param {number} page New page number.
	 */
	const handlePageChange = ( page ) => {
		setCurrentPage( page );
	};

	/**
	 * Handle items per page change
	 *
	 * @param {number} perPage New items per page.
	 */
	const handleItemsPerPageChange = ( perPage ) => {
		setItemsPerPage( perPage );
		setCurrentPage( 1 ); // Reset to first page when changing page size
	};

	/**
	 * Handle live search input. Resets to page 1 (no-op when already there).
	 *
	 * @param {string} value New search string.
	 */
	const handleSearchChange = ( value ) => {
		setSearchInput( value );
		if ( currentPage !== 1 ) {
			setCurrentPage( 1 );
		}
	};

	/**
	 * Handle a filter change. Merges a partial update and resets to page 1.
	 *
	 * @param {Object} partial Partial filter object to merge.
	 */
	const handleFilterChange = ( partial ) => {
		setFilters( ( prev ) => ( { ...prev, ...partial } ) );
		setCurrentPage( 1 );
	};

	/**
	 * Handle sortable column header click.
	 *
	 * @param {string} orderby   Sort key to activate.
	 * @param {string} direction Sort direction ('ASC' | 'DESC').
	 */
	const handleSortChange = ( orderby, direction ) => {
		setSort( { orderby, order: direction } );
		setCurrentPage( 1 );
	};

	/**
	 * Clear all filters, search, and sort.
	 */
	const handleClearFilters = () => {
		setSearchInput( '' );
		setFilters( DEFAULT_FILTERS );
		setSort( DEFAULT_SORT );
		setCurrentPage( 1 );
	};

	/**
	 * Handle booking deletion
	 *
	 * @param {number} bookingId The booking ID to delete.
	 */
	const handleDeleteBooking = async ( bookingId ) => {
		if (
			! confirm(
				__(
					'Are you sure you want to delete this booking?',
					'nemtly-booking'
				)
			)
		) {
			return;
		}

		try {
			await apiFetch( {
				path: `/nemtly-booking/v1/bookings/${ bookingId }`,
				method: 'DELETE',
			} );

			// Show success message
			setNotices( [
				{
					type: 'success',
					message: __(
						'Booking deleted successfully.',
						'nemtly-booking'
					),
				},
			] );

			// If current page is empty after deletion and we're not on page 1, go back one page
			if ( bookings.length === 1 && currentPage > 1 ) {
				setCurrentPage( currentPage - 1 );
			} else {
				// Refresh the bookings list - stay on current page
				await loadBookings(
					currentPage,
					itemsPerPage,
					filters,
					searchQuery,
					sort
				);
			}
		} catch ( err ) {
			setNotices( [
				{
					type: 'error',
					message:
						err.message ||
						__( 'Failed to delete booking.', 'nemtly-booking' ),
				},
			] );
		}
	};

	/**
	 * Handle booking status update
	 *
	 * @param {number} bookingId The booking ID to update.
	 * @param {string} status    New status.
	 */
	const handleStatusUpdate = async ( bookingId, status ) => {
		try {
			await apiFetch( {
				path: `/nemtly-booking/v1/bookings/${ bookingId }`,
				method: 'PUT',
				data: {
					booking_status: status,
				},
			} );

			// Show success message
			setNotices( [
				{
					type: 'success',
					message: __(
						'Booking status updated successfully.',
						'nemtly-booking'
					),
				},
			] );

			// Refresh the bookings list
			await loadBookings(
				currentPage,
				itemsPerPage,
				filters,
				searchQuery,
				sort
			);
		} catch ( err ) {
			setNotices( [
				{
					type: 'error',
					message:
						err.message ||
						__(
							'Failed to update booking status.',
							'nemtly-booking'
						),
				},
			] );
		}
	};

	/**
	 * Refresh bookings list
	 */
	const handleRefresh = () => {
		loadBookings( currentPage, itemsPerPage, filters, searchQuery, sort );
	};

	/**
	 * Handle create new booking
	 */
	const handleCreateNew = () => {
		if ( onCreateNew ) {
			onCreateNew();
		} else {
			// Default navigation to booking creation page
			window.location.href =
				'admin.php?page=nemtly-booking-create-booking';
		}
	};

	if ( isLoading && bookings.length === 0 ) {
		return (
			<div className="nb-text-center nb-p-8">
				<Spinner />
				<p>{ __( 'Loading bookings…', 'nemtly-booking' ) }</p>
			</div>
		);
	}

	// Prepare pagination object
	const paginationProps =
		totalPages > 1
			? {
					currentPage,
					totalPages,
					totalItems,
					itemsPerPage,
					onPageChange: handlePageChange,
					onItemsPerPageChange: handleItemsPerPageChange,
			  }
			: null;

	return (
		<div className="nb-booking-list">
			{ notices.map( ( notice, index ) => (
				<Notice
					key={ index }
					status={ notice.type }
					onRemove={ () =>
						setNotices( ( prev ) =>
							prev.filter( ( _, i ) => i !== index )
						)
					}
				>
					{ notice.message }
				</Notice>
			) ) }
			<div className="nb-flex nb-m-4 nb-items-center nb-justify-between">
				<div className="sm:nb-flex-auto">
					<h1 className="nb-text-base nb-font-semibold nb-text-neutral-900">
						{ __( 'Bookings', 'nemtly-booking' ) }
					</h1>
					<p className="nb-mt-2 nb-text-sm nb-text-neutral-500">
						{ __(
							'Manage your bookings here. You can add, edit, or delete bookings as needed.',
							'nemtly-booking'
						) }
					</p>
				</div>
				<div className="nb-mt-4 sm:nb-ml-16 sm:nb-mt-0 sm:nb-flex-none">
					<button
						type="button"
						onClick={ onCreateNew }
						className="nemtly-button-primary"
					>
						{ __( 'Add New Booking', 'nemtly-booking' ) }
					</button>
				</div>
			</div>
			<BookingFilters
				searchValue={ searchInput }
				onSearchChange={ handleSearchChange }
				filters={ filters }
				onFilterChange={ handleFilterChange }
				onClear={ handleClearFilters }
				hasActiveFilters={ hasActiveFilters }
			/>
			<div className="nb-booking-list-content">
				{ bookings.length === 0 && ! isLoading ? (
					<div className="nb-text-center nb-py-12">
						<div className="nb-text-neutral-400 nb-text-6xl nb-mb-4">
							📋
						</div>
						<h3 className="nb-text-lg nb-font-medium nb-mb-2 nb-text-neutral-900">
							{ __( 'No bookings found', 'nemtly-booking' ) }
						</h3>
						<p className="nb-text-neutral-500 nb-mb-4">
							{ hasActiveFilters
								? __(
										'No bookings match your current filters.',
										'nemtly-booking'
								  )
								: __(
										'There are no bookings yet.',
										'nemtly-booking'
								  ) }
						</p>
					</div>
				) : (
					<>
						<BookingListTable
							bookings={ bookings }
							onDeleteBooking={ handleDeleteBooking }
							onEditBooking={ onEditBooking }
							onStatusUpdate={ handleStatusUpdate }
							onCreateNew={ handleCreateNew }
							isLoading={ isLoading }
							sort={ sort }
							onSortChange={ handleSortChange }
						/>

						{ paginationProps && (
							<Pagination
								{ ...paginationProps }
								disabled={ isLoading }
								showInfo={ true }
								showItemsPerPage={ true }
							/>
						) }
					</>
				) }
			</div>
		</div>
	);
};

export default BookingList;
